/**
 * Normalización universal de nombres de cursos.
 * Refleja la misma lógica que datofy_abreviar_curso() en el plugin PHP.
 *
 * Formatos soportados de la DB:
 *   "110:Enseñanza Básica - 3 - A"               → "3A"
 *   "310:Enseñanza Media Humanístico - Segundo Año - B" → "2MB"
 *   "10:Educación Parvularia - Transición 1er Nivel - A" → "PKA"
 *   "10:Educación Parvularia - 4 - A"             → "PKA"
 *   "10:Educación Parvularia - Transición 2º Nivel - B" → "KB"
 *   "10:Educación Parvularia - 5 - B"             → "KB"
 *   "Segundo Año Medio - B"                       → "2MB"
 *   "Tercero Año Básico - A"                      → "3A"
 *   "segundo medio b"                             → "2MB"
 *   "PKA", "3A"  (ya cortos)                      → pasan directo
 */

const GRADE_MAP = {
  'Pre Kinder A': 'PK', 'Pre Kinder B': 'PK', 'Pre Kinder': 'PK',
  'Kinder A': 'K', 'Kinder B': 'K', 'Kinder - A': 'K', 'Kinder - B': 'K',
  'Primer Año Básico - A': '1BA',  'Primer Año Básico - B': '1BB',
  'Segundo Año Básico - A': '2BA', 'Segundo Año Básico - B': '2BB',
  'Tercer Año Básico - A': '3BA',  'Tercer Año Básico - B': '3BB',
  'Cuarto Año Básico - A': '4BA',  'Cuarto Año Básico - B': '4BB',
  'Quinto Año Básico - A': '5BA',  'Quinto Año Básico - B': '5BB',
  'Sexto Año Básico - A': '6BA',   'Sexto Año Básico - B': '6BB',
  'Septimo Año Básico - A': '7BA', 'Septimo Año Básico - B': '7BB',
  'Octavo Año Básico - A': '8BA',  'Octavo Año Básico - B': '8BB',
  'Primer Año Medio - A': '1MA',   'Primer Año Medio - B': '1MB',
  'Segundo Año Medio - A': '2MA',  'Segundo Año Medio - B': '2MB',
  'Tercer Año Medio - A': '3MA',   'Tercer Año Medio - B': '3MB',
  'Cuarto Año Medio - A': '4MA',   'Cuarto Año Medio - B': '4MB',
};

const norm = (s) =>
  s.replace(/[áÁ]/g,'a').replace(/[éÉ]/g,'e').replace(/[íÍ]/g,'i')
   .replace(/[óÓ]/g,'o').replace(/[úÚü]/g,'u').replace(/[ñÑ]/g,'n')
   .replace(/[º°]/g,'o')
   .toLowerCase()
   .replace(/\s+/g,' ')
   .trim();

const NUM_MAP = [
  { words: ['primer','primero','primera'], num: 1 },
  { words: ['segundo','segunda'],          num: 2 },
  { words: ['tercer','tercero','tercera'], num: 3 },
  { words: ['cuarto','cuarta'],            num: 4 },
  { words: ['quinto','quinta'],            num: 5 },
  { words: ['sexto','sexta'],              num: 6 },
  { words: ['septimo','septima'],          num: 7 },
  { words: ['octavo','octava'],            num: 8 },
];

export const normalizeCourseName = (groupName) => {
  if (!groupName || groupName === 'Sin Grupo Asignado' || groupName === 'Sin curso') {
    return groupName;
  }

  if (GRADE_MAP[groupName]) return GRADE_MAP[groupName];

  const cleaned = norm(groupName);

  for (const [key, val] of Object.entries(GRADE_MAP)) {
    if (norm(key) === cleaned) return val;
  }

  const trimmed = groupName.trim();
  if (/^(pk|k)$/i.test(trimmed)) return trimmed.toUpperCase();
  if (/^\d+[mb][ab]$/i.test(trimmed)) return trimmed.toUpperCase();

  const prefixMatch = cleaned.match(/^(\d+):(.+)/);
  let workStr = cleaned;
  let prefixCode = null;
  if (prefixMatch) {
    prefixCode = parseInt(prefixMatch[1]);
    workStr = prefixMatch[2].trim();
  }

  const letterMatch = workStr.match(/[-\s]+([ab])\s*$/i);
  const letter = letterMatch ? letterMatch[1].toUpperCase() : null;

  // Parvularia: Pre Kinder y Kinder son cursos únicos (sin letra A/B)
  if (prefixCode === 10 || workStr.includes('parvularia')) {
    if (/transicion\s*(1|1er|primer)|[-\s]+4\s*[-\s]/.test(workStr)) return 'PK';
    if (/transicion\s*(2|2o|segundo)|[-\s]+5\s*[-\s]/.test(workStr)) return 'K';
    return 'PK';
  }

  if (/pre[\s-]*kinder/.test(workStr)) return 'PK';
  if (/^kinder/.test(workStr))         return 'K';

  const isMedia = prefixCode === 310 ||
    /\bmedio\b|\bmedia\b|humanist|cientifico/.test(workStr);

  let gradeNum = null;
  const midDigit = workStr.match(/[-\s]+(\d)\s*[-\s]+[ab]/i);
  if (midDigit) gradeNum = parseInt(midDigit[1]);
  if (!gradeNum) {
    const startDigit = workStr.match(/^(\d)/);
    if (startDigit) gradeNum = parseInt(startDigit[1]);
  }
  if (!gradeNum) {
    for (const { words, num } of NUM_MAP) {
      if (words.some(w => workStr.includes(w))) { gradeNum = num; break; }
    }
  }

  if (!gradeNum || !letter) return groupName;
  return isMedia ? `${gradeNum}M${letter}` : `${gradeNum}B${letter}`;
};

/**
 * Convierte un código corto a etiqueta legible para mostrar en UI.
 * PKA → "Pre Kinder A", 2MB → "2° Medio B", 3A → "3° Básico A"
 */
export const codeToDisplayLabel = (code) => {
  if (!code) return code;

  const c = code.toUpperCase().trim();

  if (c === 'PK') return 'Pre Kinder';
  if (c === 'K')  return 'Kinder';

  // Media: 1MA, 2MB ...
  const mediaMatch = c.match(/^(\d)M([AB])$/);
  if (mediaMatch) return `${mediaMatch[1]}° Medio ${mediaMatch[2]}`;

  // Básica: 1BA, 2BB ...
  const basicaMatch = c.match(/^(\d)B([AB])$/);
  if (basicaMatch) return `${basicaMatch[1]}° Básico ${basicaMatch[2]}`;

  return code;
};

/**
 * Toma el array de group_name crudos de la DB y construye las opciones
 * del dropdown agrupando variantes del mismo curso.
 *
 * Retorna: [{ code, label, rawValues }]
 * ordenado pedagógicamente: PK → K → 1B…8B → 1M…4M
 */
export const buildNormalizedGroupOptions = (rawGroups) => {
  const map = {};

  for (const raw of rawGroups) {
    const code = normalizeCourseName(raw);
    if (!code || code === raw) {
      // No se pudo normalizar: usar el valor crudo como opción individual
      if (!map[raw]) map[raw] = { code: raw, label: raw, rawValues: [] };
      map[raw].rawValues.push(raw);
    } else {
      if (!map[code]) map[code] = { code, label: codeToDisplayLabel(code), rawValues: [] };
      map[code].rawValues.push(raw);
    }
  }

  const courseOrder = (code) => {
    const c = code.toUpperCase();
    if (c === 'PK') return 0;
    if (c === 'K')  return 1;
    const mediaM = c.match(/^(\d)M[AB]$/);
    if (mediaM) return 20 + parseInt(mediaM[1]);
    const basicaM = c.match(/^(\d)B[AB]$/);
    if (basicaM) return 10 + parseInt(basicaM[1]);
    return 99;
  };

  return Object.values(map).sort((a, b) => {
    const diff = courseOrder(a.code) - courseOrder(b.code);
    if (diff !== 0) return diff;
    return a.code.localeCompare(b.code);
  });
};

// ─── Glosario de cursos (fetch compartido) ───────────────────────────────────
// Varias pantallas (Alumnos, Asistencia, Salidas Pedagógicas) piden el mismo
// glosario crudo de cursos a /datofy/v1/user-groups por separado, cada una con
// su propio fetch inline. Este helper centraliza la petición con cache +
// dedup de peticiones en vuelo, para que montajes casi simultáneos de varias
// pantallas no disparen múltiples GET idénticos. Cada call site sigue siendo
// dueño de su propia normalización/fallback sobre el array `groups` crudo.
const USER_GROUPS_CACHE_TTL = 5 * 60 * 1000; // 5 minutos
let _userGroupsCache = null;
let _userGroupsCacheTime = null;
let _userGroupsInFlight = null;

export const getUserGroups = async (forceRefresh = false) => {
  const now = Date.now();
  if (!forceRefresh && _userGroupsCache && _userGroupsCacheTime && (now - _userGroupsCacheTime < USER_GROUPS_CACHE_TTL)) {
    return _userGroupsCache;
  }
  if (!forceRefresh && _userGroupsInFlight) {
    return _userGroupsInFlight;
  }

  _userGroupsInFlight = (async () => {
    try {
      // Import dinámico para evitar dependencia circular con services/woo.js
      const { getCredentials, getConfig } = await import('services/woo');
      const credentials = getCredentials();
      const wpApiUrl = getConfig(credentials, 'WP_API_URL') || 'https://pagos.blaspascal.cl/wp-json';
      const res = await fetch(`${wpApiUrl}/datofy/v1/user-groups`);
      if (!res.ok) throw new Error('user-groups fetch failed');
      const data = await res.json();
      const groups = data.groups || [];
      _userGroupsCache = groups;
      _userGroupsCacheTime = Date.now();
      return groups;
    } finally {
      _userGroupsInFlight = null;
    }
  })();

  return _userGroupsInFlight;
};
